/* ----------------- React Native --------------- */
import { type CursorValue, type FlexAlignType, Platform } from "react-native";

/* ----------------- React Native Paper --------------- */
import { List, type ListItemProps, Text } from "react-native-paper";

/* ----------------- Internal Types --------------- */
import type { Distribution } from "../../types";

/* ----------------- Hooks --------------- */
import { useAppTheme } from "../../useAppTheme";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import type { IconType } from "../../index";
/* ----------------- Components --------------- */
import CustomChip from "../CustomChip";
import Image from "../CustomImage";
import DonutChart from "../DonutChart";
import TooltipWrapper from "../TooltipWrapper";

const isWeb = Platform.OS === "web";
interface Style {
	marginLeft?: number;
	marginRight?: number;
	marginVertical?: number;
	alignSelf?: FlexAlignType;
}
export type MasteryListItemProps = {
	title: string;
	divider?: boolean;
	header?: boolean;
	distribution: Distribution;
	condensed?: boolean;
	locked?: boolean;
	variant?: "child" | "subchild" | "large";
	rightText?: string;
	tooltip?: string;
	iconUrl?: string;
	renderRight?: (p: {
		color: string;
		style?: Style;
	}) => React.ReactNode;
	onPress?: () => void;
	icon?: IconType;
	score?: number;
	numItems?: number;
	colorScale?: string[];
	id?: number;
} & Omit<ListItemProps, "id">;

const getRadius = (variant?: "child" | "subchild" | "large") => {
	switch (variant) {
		case "child":
			return {
				innerRadius: 10,
				radius: 14,
			};
		case "subchild":
			return {
				innerRadius: 0,
				radius: 12,
			};
		case "large":
			return {
				innerRadius: 22,
				radius: 28,
			};
		default:
			return {
				innerRadius: 22,
				radius: 28,
			};
	}
};
export const MasteryListItem = (props: MasteryListItemProps) => {
	const {
		title,
		distribution,
		titleNumberOfLines = 2,
		header,
		disabled,
		locked,
		variant,
		description,
		rightText,
		onPress,
		icon,
		tooltip,
		iconUrl,
		renderRight,
		score,
		numItems = 0,
		colorScale,
	} = props;

	const { innerRadius, radius } = getRadius(variant);
	const theme = useAppTheme();

	const renderLeft = (p: { color: string; style: Style }) => {
		return (
			<List.Icon
				{...p}
				// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
				icon={() => {
					return (
						<DonutChart
							distribution={
								score != null ? [score, numItems - score] : distribution
							}
							innerRadius={header ? 10 : innerRadius}
							radius={header ? 18 : radius}
							variant={score != null ? "progress" : "mastery"}
							score={score}
							colorScale={colorScale}
						>
							{locked ? (
								<Icon
									name="lock-outline"
									color={theme.colors.onSurfaceVariant}
									size={24}
								/>
							) : iconUrl ? (
								<Image
									source={{
										uri: iconUrl,
									}}
									style={{
										width: 36,
										height: 36,
									}}
								/>
							) : (
								icon && (
									<Icon
										name={icon}
										size={24}
										color={theme.colors.onSurfaceVariant}
									/>
								)
							)}
						</DonutChart>
					);
				}}
			/>
		);
	};

	const handleRenderRight = (p: {
		color: string;
		style?: Style;
	}) => {
		if (renderRight) {
			return renderRight(p);
		}
		return (
			<>
				{rightText ? (
					<List.Icon
						{...p}
						icon={() =>
							isWeb ? (
								<TooltipWrapper title={tooltip || ""}>
									<CustomChip
										{...p}
										textStyle={{
											cursor: "help" as unknown as CursorValue,
										}}
									>
										{rightText}
									</CustomChip>
								</TooltipWrapper>
							) : (
								<CustomChip {...p}>{rightText}</CustomChip>
							)
						}
					/>
				) : null}
			</>
		);
	};
	return (
		<List.Item
			onPress={onPress}
			title={title}
			style={{
				cursor: onPress ? "pointer" : "auto",
			}}
			titleEllipsizeMode="tail"
			description={
				description && (
					<Text variant="bodySmall">{description as React.ReactNode}</Text>
				)
			}
			titleStyle={{
				fontSize: variant === "large" ? 16 : 14,
			}}
			titleNumberOfLines={titleNumberOfLines}
			disabled={disabled}
			left={renderLeft}
			right={handleRenderRight}
		/>
	);
};
